import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Gift, Star, Heart, Users, Baby } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function RatgeberKinderPage() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]" data-testid="ratgeber-kinder-page">
      {/* Hero */}
      <section className="py-12 md:py-20 bg-[#F5F5F0]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 text-[#4A5D4F] hover:text-[#3A493E] mb-6">
            <ArrowLeft className="w-4 h-4" />
            Alle Ratgeber
          </Link>
          <div className="flex items-center gap-2 text-sm text-[#6B7280] mb-4">
            <Calendar className="w-4 h-4" />
            <span>Aktualisiert: Februar 2026</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-[#2C332E] mb-6 leading-tight">
            Top 10 Eid Geschenke für Kinder 2026
          </h1>
          <p className="text-lg text-[#6B7280] leading-relaxed">
            Entdecke die schönsten Eid Mubarak Geschenke für Kinder jeden Alters. 
            Von islamischen Kinderbüchern bis zu kreativen Spielsets – hier findest du 
            liebevoll ausgewählte Geschenkideen, die Kinderaugen zum Leuchten bringen.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-[#6B7280] leading-relaxed mb-8">
              Eid ist für Kinder eine ganz besondere Zeit voller Freude und Aufregung. 
              Das richtige <strong>Eid Geschenk für Kinder</strong> zu finden, kann jedoch 
              eine Herausforderung sein. Wir haben die besten Geschenkideen für 2026 
              zusammengestellt, die nicht nur Spaß machen, sondern auch einen pädagogischen 
              Mehrwert bieten.
            </p>

            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mt-12 mb-6 flex items-center gap-3">
              <Star className="w-6 h-6 text-[#D4C5A9]" />
              Unsere Top 10 Eid Geschenke für Kinder
            </h2>

            <div className="space-y-6">
              {[
                { num: 1, title: 'Islamische Kinderbücher', desc: 'Bücher wie "Mama, was ist Ramadan?" erklären den Islam kindgerecht und fördern das Verständnis für die eigene Religion.' },
                { num: 2, title: 'Eid Überraschungsboxen', desc: 'Liebevoll zusammengestellte Boxen mit Süßigkeiten, kleinen Spielzeugen und Eid-Karten sorgen für strahlende Kinderaugen.' },
                { num: 3, title: 'Kreativ-Sets zum Basteln', desc: 'Malstifte, Bastelpapier und Anleitungen für Eid-inspirierte Kunstprojekte fördern die Kreativität.' },
                { num: 4, title: 'Islamische Lernspiele', desc: 'Quiz-Karten über den Propheten oder den Quran machen das Lernen zum Familienspaß.' },
                { num: 5, title: 'Kuscheltiere mit Eid-Outfit', desc: 'Plüschtiere in festlicher Kleidung werden zu treuen Begleitern für die Eid-Feierlichkeiten.' },
                { num: 6, title: 'Eid Malbücher', desc: 'Altersgerechte Malbücher mit islamischen Motiven verbinden Spaß und Lernen.' },
                { num: 7, title: 'Gebetsketten für Kinder', desc: 'Bunte Tasbih speziell für kleine Hände – ein erstes Geschenk für die spirituelle Entwicklung.' },
                { num: 8, title: 'Eid Dekoration fürs Kinderzimmer', desc: 'Lichterketten, Wandbilder oder Girlanden mit Eid Mubarak Motiven.' },
                { num: 9, title: 'Süßigkeiten-Körbe', desc: 'Halal-Süßigkeiten, Datteln und Nüsse in einem festlichen Korb verpackt.' },
                { num: 10, title: 'Spardosen mit islamischem Design', desc: 'Lehrt Kindern den Wert des Sparens und der Sadaqa auf spielerische Weise.' },
              ].map((item) => (
                <div key={item.num} className="flex gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#4A5D4F] flex items-center justify-center">
                    <span className="text-white font-bold">{item.num}</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-[#2C332E] mb-1">{item.title}</h3>
                    <p className="text-sm text-[#6B7280]">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mt-12 mb-6 flex items-center gap-3">
              <Heart className="w-6 h-6 text-[#D4C5A9]" />
              Tipps für das perfekte Eid Geschenk
            </h2>

            <ul className="space-y-3 text-[#6B7280]">
              <li className="flex items-start gap-2">
                <Gift className="w-5 h-5 text-[#4A5D4F] flex-shrink-0 mt-0.5" />
                <span><strong>Altersgerecht wählen:</strong> Achte darauf, dass das Geschenk zum Alter des Kindes passt.</span>
              </li>
              <li className="flex items-start gap-2">
                <Gift className="w-5 h-5 text-[#4A5D4F] flex-shrink-0 mt-0.5" />
                <span><strong>Pädagogischer Wert:</strong> Geschenke, die Lernen und Spaß verbinden, bleiben lange in Erinnerung.</span>
              </li>
              <li className="flex items-start gap-2">
                <Gift className="w-5 h-5 text-[#4A5D4F] flex-shrink-0 mt-0.5" />
                <span><strong>Islamische Werte:</strong> Wähle Geschenke, die die Verbindung zum Islam stärken.</span>
              </li>
              <li className="flex items-start gap-2">
                <Gift className="w-5 h-5 text-[#4A5D4F] flex-shrink-0 mt-0.5" />
                <span><strong>Rechtzeitig bestellen:</strong> Plane genug Vorlaufzeit für die Lieferung vor Eid ein.</span>
              </li>
            </ul>

            <div className="mt-12 p-6 bg-[#4A5D4F] rounded-2xl text-center">
              <p className="text-white mb-4">
                Entdecke jetzt alle Eid Geschenke für Kinder in unserer Auswahl!
              </p>
              <Button asChild className="bg-white text-[#4A5D4F] hover:bg-[#F5F5F0] rounded-full px-8 py-3">
                <Link to="/geschenke/kinder">
                  Kinder-Geschenke entdecken
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}
